import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, 
  Download, 
  Bell, 
  Wifi, 
  WifiOff, 
  Database, 
  Trash2,
  RefreshCw,
  Info,
  X,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';
import { usePWA } from '../../hooks/usePWA';
import { offlineStorage, syncOfflineData } from '../../utils/offline-storage';
import { pwa } from '../../utils/pwa';

interface PWADebuggerProps {
  show: boolean;
  onClose: () => void;
}

export const PWADebugger: React.FC<PWADebuggerProps> = ({ show, onClose }) => {
  const {
    isInstalled,
    canInstall,
    isOnline,
    updateAvailable,
    notificationPermission,
    installApp,
    updateApp,
    requestNotifications,
    syncOfflineData: syncData
  } = usePWA();

  const [storageInfo, setStorageInfo] = useState<any>(null);
  const [syncQueue, setSyncQueue] = useState<any[]>([]);
  const [offlineData, setOfflineData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('status');
  const [syncing, setSyncing] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (show) {
      loadDebugInfo();
    }
  }, [show]);

  const loadDebugInfo = async () => {
    try {
      // Storage info
      const storage = await pwa.getStorageEstimate();
      setStorageInfo(storage);

      // Sync queue
      const queue = await offlineStorage.getSyncQueue();
      setSyncQueue(queue);

      // Offline data summary
      const meals = await offlineStorage.getMeals(5);
      const nutritionData = await offlineStorage.getRecentNutritionData(7);
      
      setOfflineData({
        meals: meals.length,
        nutrition: nutritionData.length,
        unsyncedMeals: meals.filter(m => !m.synced).length
      });
    } catch (error) {
      console.error('Failed to load debug info:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncOfflineData();
      await loadDebugInfo();
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleClearData = async () => {
    setClearing(true);
    try {
      await offlineStorage.clearAllData();
      await loadDebugInfo();
    } catch (error) {
      console.error('Clear data failed:', error);
    } finally {
      setClearing(false);
    }
  };

  const handleExportData = async () => {
    try {
      const data = await offlineStorage.exportData();
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `nutriquests-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleImportData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      await offlineStorage.importData(text);
      await loadDebugInfo();
    } catch (error) {
      console.error('Import failed:', error);
    }
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  PWA Debugger
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Development and debugging tools
                </p>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {[
              { id: 'status', label: 'Status', icon: Info },
              { id: 'storage', label: 'Storage', icon: Database },
              { id: 'sync', label: 'Sync', icon: RefreshCw },
              { id: 'actions', label: 'Actions', icon: Settings }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-b-2 border-emerald-600'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-4 max-h-96 overflow-y-auto">
            {activeTab === 'status' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <StatusCard
                    title="App Status"
                    value={isInstalled ? 'Installed' : 'Browser'}
                    icon={isInstalled ? CheckCircle : Download}
                    color={isInstalled ? 'green' : 'blue'}
                  />
                  
                  <StatusCard
                    title="Network"
                    value={isOnline ? 'Online' : 'Offline'}
                    icon={isOnline ? Wifi : WifiOff}
                    color={isOnline ? 'green' : 'orange'}
                  />
                  
                  <StatusCard
                    title="Notifications"
                    value={notificationPermission}
                    icon={Bell}
                    color={notificationPermission === 'granted' ? 'green' : 'gray'}
                  />
                  
                  <StatusCard
                    title="Updates"
                    value={updateAvailable ? 'Available' : 'Current'}
                    icon={updateAvailable ? AlertCircle : CheckCircle}
                    color={updateAvailable ? 'orange' : 'green'}
                  />
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    Environment Info
                  </h3>
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <p>User Agent: {navigator.userAgent.split(' ').slice(-2).join(' ')}</p>
                    <p>Viewport: {window.innerWidth} × {window.innerHeight}</p>
                    <p>Display Mode: {window.matchMedia('(display-mode: standalone)').matches ? 'Standalone' : 'Browser'}</p>
                    <p>Service Worker: {'serviceWorker' in navigator ? 'Supported' : 'Not supported'}</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'storage' && (
              <div className="space-y-4">
                {storageInfo && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                      Storage Usage
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Used</span>
                        <span className="font-medium">{Math.round(storageInfo.usage / 1024 / 1024)} MB</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Available</span>
                        <span className="font-medium">{Math.round(storageInfo.available / 1024 / 1024)} MB</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-emerald-600 h-2 rounded-full"
                          style={{ width: `${storageInfo.percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {offlineData && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {offlineData.meals}
                      </div>
                      <div className="text-sm text-blue-800 dark:text-blue-200">
                        Cached Meals
                      </div>
                    </div>
                    
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {offlineData.nutrition}
                      </div>
                      <div className="text-sm text-green-800 dark:text-green-200">
                        Nutrition Data
                      </div>
                    </div>
                    
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                      <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                        {offlineData.unsyncedMeals}
                      </div>
                      <div className="text-sm text-orange-800 dark:text-orange-200">
                        Unsynced
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'sync' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    Sync Queue ({syncQueue.length} items)
                  </h3>
                  
                  <button
                    onClick={handleSync}
                    disabled={syncing || !isOnline}
                    className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>

                {syncQueue.length > 0 ? (
                  <div className="space-y-2">
                    {syncQueue.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.method} {item.endpoint}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(item.timestamp).toLocaleString()} • {item.retries} retries
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    <RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No items in sync queue</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'actions' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={installApp}
                    disabled={!canInstall}
                    className="flex items-center justify-center gap-2 p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    Install App
                  </button>
                  
                  <button
                    onClick={requestNotifications}
                    disabled={notificationPermission === 'granted'}
                    className="flex items-center justify-center gap-2 p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Bell className="w-4 h-4" />
                    Enable Notifications
                  </button>
                  
                  <button
                    onClick={handleExportData}
                    className="flex items-center justify-center gap-2 p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Export Data
                  </button>
                  
                  <label className="flex items-center justify-center gap-2 p-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors cursor-pointer">
                    <Database className="w-4 h-4" />
                    Import Data
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportData}
                      className="hidden"
                    />
                  </label>
                  
                  <button
                    onClick={updateApp}
                    disabled={!updateAvailable}
                    className="flex items-center justify-center gap-2 p-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Update App
                  </button>
                  
                  <button
                    onClick={handleClearData}
                    disabled={clearing}
                    className="flex items-center justify-center gap-2 p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4" />
                    {clearing ? 'Clearing...' : 'Clear Data'}
                  </button>
                </div>
                
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Development Mode</span>
                  </div>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    This debugger is only available in development mode and will not appear in production builds.
                  </p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const StatusCard: React.FC<{
  title: string;
  value: string;
  icon: React.ComponentType<any>;
  color: 'green' | 'blue' | 'orange' | 'gray';
}> = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    green: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    blue: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    orange: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20',
    gray: 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50'
  };

  return (
    <div className={`rounded-lg p-3 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{title}</span>
      </div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
};

// Development-only component wrapper
export const PWADebuggerTrigger: React.FC = () => {
  const [show, setShow] = useState(false);

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setShow(true)}
        className="fixed bottom-4 left-4 p-3 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors z-40"
        title="PWA Debugger"
      >
        <Settings className="w-5 h-5" />
      </button>
      
      <PWADebugger show={show} onClose={() => setShow(false)} />
    </>
  );
};

export default PWADebugger;